import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/use-language';
import { getPagePath, type Lang } from '@/lib/localizedRoutes';
import {
  Facebook, Instagram, Linkedin, Youtube, Send, Twitter, MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackClick } from '@/lib/analytics';
import {
  getCachedFooterData, fetchFooterData, subscribeToCache,
  type FooterData,
} from '@/lib/globalDataCache';

export interface FooterProps {
  className?: string;
}

// Default values shown before DB data loads (matches admin defaults)
const DEFAULTS: FooterData = {
  info: {
    ru: { description: 'Инновационные системы и решения для современных интерьеров', copyright: '© 2026 Sargo Industrial Systems. Все права защищены.' },
    uz: { description: 'Zamonaviy interyer uchun innovatsion tizimlar va yechimlar', copyright: '© 2026 Sargo Industrial Systems. Barcha huquqlar himoyalangan.' },
    en: { description: 'Innovative systems and solutions for modern interiors', copyright: '© 2026 Sargo Industrial Systems. All rights reserved.' },
  },
  contacts: {
    ru: { address: 'ул. Промышленная, 1, Ташкент, Узбекистан', phone: '+998 (71) 200-00-00', email: 'info@sargo.uz' },
    uz: { address: "Sanoat ko'chasi, 1, Toshkent, O'zbekiston", phone: '+998 (71) 200-00-00', email: 'info@sargo.uz' },
    en: { address: '1 Industrial St., Tashkent, Uzbekistan', phone: '+998 (71) 200-00-00', email: 'info@sargo.uz' },
  },
  social: {
    facebook: 'https://www.facebook.com/profile.php?id=61584716230422',
    instagram: 'https://www.instagram.com/sargo.uz',
    linkedin: 'https://www.linkedin.com/company/110521447',
    youtube: 'https://youtube.com/@sargo.company',
    telegram: '', twitter: '', vk: '',
  },
};

// Social network icon map
const SOCIAL_ICONS: Record<string, React.ElementType> = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  telegram: Send,
  twitter: Twitter,
  vk: MessageCircle,
};

const SOCIAL_KEYS = ['facebook', 'instagram', 'linkedin', 'youtube', 'telegram', 'twitter', 'vk'] as const;

export const Footer: React.FC<FooterProps> = ({ className }) => {
  const { t, language } = useLanguage();

  // Initialize synchronously from cache (localStorage-backed) — zero delay on repeat visits
  const [footerData, setFooterData] = useState<FooterData>(() => getCachedFooterData() ?? DEFAULTS);

  useEffect(() => {
    // If cache already has data, use it immediately
    const cached = getCachedFooterData();
    if (cached) setFooterData(cached);

    // Fetch fresh data in background (deduped, updates cache)
    fetchFooterData().then(data => setFooterData(data)).catch(() => {});

    // Subscribe to cache updates (e.g. after admin save)
    const unsub = subscribeToCache(() => {
      const updated = getCachedFooterData();
      if (updated) setFooterData(updated);
    });
    return unsub;
  }, []);

  // Re-read from cache on language change (data is already cached, just re-render)
  // No extra fetch needed — same footerData object covers all langs
  const lang = language as string;

  const info = footerData.info[lang] ?? footerData.info['ru'] ?? DEFAULTS.info['ru'];
  const contacts = footerData.contacts[lang] ?? footerData.contacts['ru'] ?? DEFAULTS.contacts['ru'];
  const social = footerData.social;

  // Build active social links
  const activeSocials = SOCIAL_KEYS
    .filter(key => social[key]?.trim())
    .map(key => ({ key, url: social[key], Icon: SOCIAL_ICONS[key] }));

  return (
    <footer className={cn("bg-background relative overflow-hidden pt-6 lg:pt-16 pb-4 lg:pb-8", "pb-[calc(1rem+env(safe-area-inset-bottom))] lg:pb-8", className)}>
      {/* SARGO Watermark */}
      <div className="absolute bottom-[-10%] right-[-5%] lg:right-0 text-[25vw] lg:text-[20vw] font-black text-white/[0.02] uppercase leading-none select-none pointer-events-none tracking-tighter z-0">
        SARGO
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-8 mb-6 lg:mb-16">

          {/* Column 1: Brand & Info */}
          <div className="col-span-1 lg:col-span-4 space-y-3 lg:space-y-8">
            <div className="space-y-1">
              <Link to={`/${language}`} className="block">
                <span className="text-xl lg:text-3xl font-black tracking-tighter text-primary">SARGO</span>
              </Link>
              <div className="w-8 lg:w-12 h-[2px] bg-primary"></div>
              <p className="text-[8px] lg:text-xs font-bold tracking-[0.2em] text-white/90">FLOORING AND WALL PANELS</p>
            </div>

            {info.description && (
              <p className="text-white/40 max-w-xs text-[8px] lg:text-xs leading-relaxed uppercase tracking-wider font-medium">
                {info.description}
              </p>
            )}

            {activeSocials.length > 0 && (
              <div className="space-y-2 lg:space-y-4 pt-1 lg:pt-0">
                <h4 className="text-[8px] lg:text-[10px] font-black uppercase tracking-[0.3em] text-primary">{t('footer.social')}</h4>
                <div className="flex flex-wrap gap-1.5 lg:gap-2">
                  {activeSocials.map(({ key, url, Icon }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackClick(`social-${key}-footer`)}
                      aria-label={`SARGO on ${key.charAt(0).toUpperCase() + key.slice(1)}`}
                      className="w-7 h-7 lg:w-10 lg:h-10 flex items-center justify-center bg-white/[0.03] border border-white/5 hover:bg-primary hover:border-primary text-white transition-all duration-300 group rounded-none"
                    >
                      <Icon size={12} className="group-hover:scale-110 transition-transform lg:w-4 lg:h-4" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Column 2: Navigation */}
          <div className="hidden lg:block lg:col-span-2 space-y-6">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">{t('footer.navigation')}</h4>
            <nav className="flex flex-col gap-4">
              {[
                { key: 'catalog', id: '01', label: t('nav.catalog') },
                { key: 'portfolio', id: '02', label: t('nav.portfolio') },
                { key: 'about', id: '03', label: t('nav.about') },
                { key: 'news', id: '04', label: t('nav.news') },
              ].map((item) => (
                <Link key={item.id} to={getPagePath(item.key, language as Lang)} className="text-white/60 hover:text-primary text-[10px] lg:text-xs font-bold uppercase tracking-widest transition-colors">
                  {item.id}. {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Column 3: Partners */}
          <div className="hidden lg:block lg:col-span-2 space-y-6">
            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">{t('footer.partners')}</h4>
            <nav className="flex flex-col gap-4">
              {[
                { key: 'become-dealer', id: '05', label: t('nav.becomeDealer') },
                { key: 'where-to-buy', id: '06', label: t('nav.whereToBuy') },
                { key: 'useful', id: '07', label: t('nav.useful') },
              ].map((item) => (
                <Link key={item.id} to={getPagePath(item.key, language as Lang)} className="text-white/60 hover:text-primary text-[10px] lg:text-xs font-bold uppercase tracking-widest transition-colors">
                  {item.id}. {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Column 4: Contacts */}
          <div className="col-span-1 lg:col-span-4 space-y-4 lg:space-y-8 pt-[50px] lg:pt-0 lg:pl-12">
            <div className="space-y-3 lg:space-y-6">
              <h4 className="text-[8px] lg:text-[10px] font-black uppercase tracking-[0.3em] text-primary">{t('footer.contacts')}</h4>
              <div className="space-y-3 lg:space-y-6">
                {contacts.address && (
                  <div className="space-y-1">
                    <p className="text-[8px] lg:text-xs text-white/90 font-bold uppercase tracking-tight leading-tight">
                      {contacts.address}
                    </p>
                  </div>
                )}
                {contacts.phone && (
                  <div className="space-y-1">
                    <span className="text-[7px] lg:text-[9px] text-white/20 uppercase tracking-[0.3em] font-black">{t('footer.phone')}</span>
                    <a
                      href={`tel:${contacts.phone.replace(/[^0-9+]/g, '')}`}
                      onClick={() => trackClick('phone-click-footer')}
                      className="block text-white/90 hover:text-primary text-[10px] lg:text-sm font-black transition-all tracking-tight"
                    >
                      {contacts.phone}
                    </a>
                  </div>
                )}
                {contacts.email && (
                  <div className="space-y-1">
                    <span className="text-[7px] lg:text-[9px] text-white/20 uppercase tracking-[0.3em] font-black">{t('footer.email')}</span>
                    <a
                      href={`mailto:${contacts.email}`}
                      onClick={() => trackClick('email-click-footer')}
                      className="block text-white/90 hover:text-primary text-[10px] lg:text-sm font-black transition-all tracking-tight lowercase"
                    >
                      {contacts.email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Copyright */}
        <div className="pt-4 lg:pt-8 border-t border-white/5">
          <p className="text-[8px] lg:text-[10px] text-white/20 font-black uppercase tracking-[0.2em] text-center lg:text-left">
            {info.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};
